import {
  getHistoricRecent,
  postLiveGrantWs,
} from "@aura-frontend/omnibus-az-client";
import React, { createContext, useCallback, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useWebSocket, { ReadyState } from "react-use-websocket";

import {
  addEventToQueue,
  addHistoricEventsToQueue,
  resetEventQueue,
} from "../../store/slices/socket";
import { useTaggingDispatch } from "../../store/store";
import type { IGameEvent, IQueueEvent } from "../../store/types";
import { eventQueueTransformer } from "../../store/utils/eventQueueTransformer";

export type BadgeStatus = "disconnected" | "connecting" | "connected";

type SocketContextType = {
  socketStatus: BadgeStatus;
};

const SocketContext = createContext<SocketContextType>({
  socketStatus: "disconnected",
});

export const useSocketContext = () => useContext(SocketContext);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useTaggingDispatch();

  const { id: machineId } = useParams() as { id: string };
  const navigate = useNavigate();

  const fetchWebSocketUrl = useCallback(async () => {
    const { data, error, response } = await postLiveGrantWs({
      query: { machineUuid: machineId },
    });

    if (error) {
      // The API returns 403 when the user doesn't have access to the machine
      if (response.status === 403) {
        navigate("/404");
      } else {
        throw error;
      }
    }

    return data?.websocket ?? "";
  }, [machineId, navigate]);

  const fetchHistoricRecentEvents = useCallback(async () => {
    try {
      const response = await getHistoricRecent({
        query: { machineUuid: machineId },
        throwOnError: true,
      });
      const events = response.data.events ?? [];
      dispatch(addHistoricEventsToQueue(events as unknown as IQueueEvent[]));
    } catch {
      // Fallback to empty array if the request fails
      return [];
    }
  }, [machineId, dispatch]);

  const socketConfigFn: () => Promise<string> = useCallback(async () => {
    const websocketUrl = await fetchWebSocketUrl();
    await fetchHistoricRecentEvents();
    return websocketUrl;
  }, [fetchWebSocketUrl, fetchHistoricRecentEvents]);

  const { readyState } = useWebSocket(socketConfigFn, {
    onOpen: () => {
      dispatch(resetEventQueue());
      console.info("Socket connection established");
    },
    onClose: () => {
      console.info("Socket connection closed");
    },
    onMessage: (event) => {
      const parsedEvent: { data: IGameEvent; from: string } = JSON.parse(
        event.data,
      );
      const cleanEvent = eventQueueTransformer(parsedEvent.data);
      dispatch(addEventToQueue(cleanEvent));
    },
    retryOnError: true,
    shouldReconnect: () => true,
    // 20 attempts, 5 seconds in between each attempt
    reconnectAttempts: 20,
    reconnectInterval: 5000,
  });

  const statusesDictionary: Record<ReadyState, BadgeStatus> = {
    [ReadyState.CONNECTING]: "connecting",
    [ReadyState.OPEN]: "connected",
    [ReadyState.CLOSING]: "disconnected",
    [ReadyState.CLOSED]: "disconnected",
    [ReadyState.UNINSTANTIATED]: "disconnected",
  };

  const socketStatus = statusesDictionary[readyState];

  return (
    <SocketContext.Provider value={{ socketStatus }}>
      {children}
    </SocketContext.Provider>
  );
};
